/**
 * Copyright © Binary Anvil, Inc. All rights reserved.
 * See https://binaryanvil.com/software/license for license details.
 */
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import React from "react";
import dynamic from 'next/dynamic'
import {PRODUCT_PAGE_BANNER_CMSBLOCK_ID, RESERVED_URLS, STATIC_DATA_REVALIDATE_PERIOD_SECONDS} from "@/app/constants";
import {getCmsPageStaticProps} from "@/app/utils/getStaticProps/getCmsPageStaticProps";
import {getCommonStaticProps} from "@/app/utils/getStaticProps/getCommonStaticProps";
import {TCmsPageAllStoresData} from "@/app/types/prefetchedData/cmsPageData";
import {TCommonStaticDataObject} from "@/app/types/prefetchedData/commonStaticData";
import getMaintenanceMode from "@/app/utils/getMaintenanceMode";
import {GetStaticProps, GetStaticPaths} from "next";
import generateDynamicPaths from "@/app/utils/getStaticPaths/generateDynamicPaths";
import {getRouteInfo} from "@/app/utils/getStaticProps/getRouteInfo";
import {getStoreCodeByLocale} from "@/app/utils/common";
import {getCategoryStaticProps} from "@/app/utils/getStaticProps/getCategoryStaticProps";
import {TCategoryDataAllStores} from "@/app/types/prefetchedData/categoryData";
import getProductStaticProps from "@/app/utils/getStaticProps/getProductStaticProps";
import {getCmsBlockStaticProps} from "@/app/utils/getStaticProps/getCmsBlockStaticProps";
import {TProductDataAllStores} from "@/app/types/product";
import {TCmsBlocksObject} from "@/app/types/prefetchedData/cmsBlocksData";
const ProductPageView = dynamic(() => import("@/app/components/views/ProductPageView"));
const CmsPageView = dynamic(() => import('@/app/components/views/CmsPageView'));
const CategoryPageView = dynamic(() => import("@/app/components/views/CategoryPageView"));
import NoRoute from "@/app/components/NoRoute";
import {TRouteInfoByStore} from "@/app/types/prefetchedData/routeInfo";
import {AdditionalProductsMaxNumType} from "@/app/types/prefetchedData/pdpCommonStaticData";
import getAdditonalMaxNum from "@/app/utils/getAdditionalProductsMaxNum";

export const getStaticPaths: GetStaticPaths = async () => {
    const {paths} = await generateDynamicPaths({
        excludedUrls: RESERVED_URLS,
    });
    return {paths, fallback: 'blocking'}
}

export const getStaticProps: GetStaticProps = async (context) => {
    let notFound = true;
    const url = (context.params?.url as string[]).join('/');
    const storeCode = getStoreCodeByLocale(context.locale);
    const routeInfo = await getRouteInfo({
        url: url,
        storeCode: storeCode,
    });

    if(storeCode) {
        if(routeInfo) {
            const currentStoreRouteInfo = routeInfo.stores[storeCode];

            if(currentStoreRouteInfo.routeInfo && currentStoreRouteInfo.routeInfo.route &&
                (currentStoreRouteInfo.routeInfo.route.redirect_code === 301 ||
                currentStoreRouteInfo.routeInfo.route.redirect_code === 302)
            ) {
                const localePrefix = context.locale !== context.defaultLocale ? `${context.locale}/` : '';
                return {
                    redirect: {
                        statusCode: currentStoreRouteInfo.routeInfo.route.redirect_code,
                        basePath: false,
                        destination: `/${localePrefix}${currentStoreRouteInfo.routeInfo.route.relative_url}`,
                    },
                    props: {},
                }
            }

            switch (currentStoreRouteInfo.routeInfo?.route?.type) {
                case 'CATEGORY':
                    const categoryStaticData = await getCategoryStaticProps({
                        filters: {
                            category_uid: {
                                eq: currentStoreRouteInfo.routeInfo.route.uid
                            }
                        },
                        storeCode: storeCode,
                    });

                    // Set not found to false if even one store has valid category data
                    notFound = categoryStaticData?.stores
                        ? !Object.values(categoryStaticData.stores).some(
                            (store) =>
                                store?.categoryData?.categoryList &&
                                store.categoryData.categoryList.length > 0
                        )
                        : true;

                    return {
                        props: {
                            ...(await serverSideTranslations(context.locale ?? '', ['common'])),
                            maintenanceMode: await getMaintenanceMode(),
                            commonStaticData: await getCommonStaticProps(),
                            routeInfo: currentStoreRouteInfo,
                            categoryPrefetchedData: categoryStaticData,
                        },
                        notFound: notFound,
                        revalidate: STATIC_DATA_REVALIDATE_PERIOD_SECONDS
                    }
                    break;

                case 'PRODUCT':
                    const productDataAllStores = currentStoreRouteInfo.routeInfo.route.sku ? await getProductStaticProps({
                        sku: currentStoreRouteInfo.routeInfo.route.sku,
                        storeCode: storeCode,
                    }) : undefined;

                    // Set not found to false if even one store has valid product data
                    const storeList = Object.values(productDataAllStores?.stores ?? {})
                    notFound = !storeList.some(store => store.notFound === false);

                    const productCmsBlockAllStores = await getCmsBlockStaticProps(PRODUCT_PAGE_BANNER_CMSBLOCK_ID);
                    const additionalProductsMaxNum = await getAdditonalMaxNum()

                    return {
                        props: {
                            ...(await serverSideTranslations(context.locale ?? '', ['common'])),
                            maintenanceMode: await getMaintenanceMode(),
                            commonStaticData: await getCommonStaticProps(),
                            routeInfo: currentStoreRouteInfo,
                            productPageData: productDataAllStores,
                            productCmsBlockData: productCmsBlockAllStores,
                            additionalProductsMaxNum: additionalProductsMaxNum,
                        },
                        notFound: notFound,
                        revalidate: STATIC_DATA_REVALIDATE_PERIOD_SECONDS
                    }
                    break;

                case 'CMS_PAGE':
                    const cmsPageStaticData = currentStoreRouteInfo.routeInfo.route.identifier ?
                        await getCmsPageStaticProps({
                            identifier: currentStoreRouteInfo.routeInfo.route.identifier,
                            storeCode: storeCode,
                        }) : undefined;

                    // Set not found to false if even one store has valid CMS page data
                    const cmsPageStoreList = Object.values(cmsPageStaticData?.stores ?? {})
                    notFound = !cmsPageStoreList.some(store => !!store.cmsPageData);

                    return {
                        props: {
                            ...(await serverSideTranslations(context.locale ?? '', ['common'])),
                            maintenanceMode: await getMaintenanceMode(),
                            commonStaticData: await getCommonStaticProps(),
                            routeInfo: currentStoreRouteInfo,
                            cmsPageData: cmsPageStaticData,
                        },
                        notFound: notFound,
                        revalidate: STATIC_DATA_REVALIDATE_PERIOD_SECONDS
                    }
                    break;
            }
        }
    }

    return {
        props: {
            ...(await serverSideTranslations(context.locale ?? '', ['common'])),
            maintenanceMode: await getMaintenanceMode(),
            renderingMode: 'SSG',
            commonStaticData: await getCommonStaticProps()
        },
        notFound: notFound,
        revalidate: STATIC_DATA_REVALIDATE_PERIOD_SECONDS
    }
}

export default function DynamicRoute(props: {
    commonStaticData: TCommonStaticDataObject,
    maintenanceMode?: boolean,
    routeInfo?: TRouteInfoByStore,
    // CMS page props
    cmsPageData?: TCmsPageAllStoresData,
    // Category page props
    categoryPrefetchedData?: TCategoryDataAllStores,
    // Product page props
    productPageData?: TProductDataAllStores,
    productCmsBlockData?: TCmsBlocksObject,
    additionalProductsMaxNum?: AdditionalProductsMaxNumType,
}) {
    const currentRouteInfo = props.routeInfo?.routeInfo?.route;
    switch (currentRouteInfo?.type) {
        case "CMS_PAGE":
            return props.cmsPageData && <CmsPageView cmsPageData={props.cmsPageData}
                                                     routeInfo={currentRouteInfo} />

        case "CATEGORY":
            return props.categoryPrefetchedData && <CategoryPageView categoryPrefetchedData={props.categoryPrefetchedData}
                                                                     routeInfo={currentRouteInfo} />

        case "PRODUCT":
            return props.productPageData && <ProductPageView routeInfo={currentRouteInfo}
                                                             productPageData={props.productPageData}
                                                             productCmsBlockData={props.productCmsBlockData}
                                                             additionalProductsMaxNum={props.additionalProductsMaxNum}
                                                              />

        default:
            console.error("Unexpected page type (dynamic routing)");
            return <NoRoute />;
    }
}